<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando orden de pago...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Pago a proveedores' }">Pago a proveedores</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <span class="view-header__breadcrumb-current">
            #{{ payorder.number || payOrderId }}
          </span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de orden de pago</h1>
            <p class="view-header__subtitle">Información detallada de la orden de pago.</p>
          </div>

          <div class="view-header__actions">
            <DownloadDoc
              :pdfurl="pdfurl"
              :open="true"
              :filename="payorder.provider_name + payorder.created_at + '.pdf'"
            >
              <button type="button" class="btn view-header__btn">
                <i class="bi bi-printer"></i>
                Imprimir
              </button>
            </DownloadDoc>
            <DownloadDoc
              :pdfurl="pdfurl"
              :open="false"
              :filename="payorder.provider_name + payorder.created_at + '.pdf'"
            >
              <button type="button" class="btn view-header__btn view-header__btn--primary">
                <i class="bi bi-download"></i>
                Descargar PDF
              </button>
            </DownloadDoc>
          </div>
        </div>
      </div>

      <!-- Header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">Orden de pago</span>
              <span v-if="payorder.number" class="invoice-type-badge">
                #{{ payorder.number }}
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-truck"></i>
              </span>
              <span>Datos del proveedor</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ payorder.provider_name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="payorder.number" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de orden
                </span>
                <span class="field-value">#{{ payorder.number }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Content + totals -->
      <div class="row">
        <!-- Tables -->
        <div class="col-md-8 mb-4 mb-md-0">
          <!-- Forma de pago -->
          <div v-if="payments.length > 0" class="card items-card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-wallet2"></i>
                </span>
                <h5 class="mb-0">Forma de pago</h5>
              </div>
              <div class="items-summary">
                <span class="items-summary-item">
                  Total de items: <strong>{{ payments.length }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Tipo</th>
                      <th>Detalle</th>
                      <th class="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, idx) in payments" :key="'pm-' + idx">
                      <td>{{ p.type }}</td>
                      <td>
                        {{ p.detail }}
                        <span
                          v-if="p.check_kind"
                          class="badge"
                          :class="p.check_kind === 'echeq' ? 'badge-info' : 'badge-secondary'"
                        >
                          {{ checkKindLabels[p.check_kind] || 'Físico' }}
                        </span>
                      </td>
                      <td class="text-right font-weight-bold">
                        {{ formatAmount(p.amount) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Comprobantes asociados -->
          <div v-if="purchases.length > 0" class="card items-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-file-earmark-check"></i>
                </span>
                <h5 class="mb-0">Comprobantes asociados</h5>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Tipo</th>
                      <th>Numero</th>
                      <th class="text-right">Importe</th>
                      <th class="text-right">En orden</th>
                      <th class="text-center">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(pu, idx) in purchases" :key="'pu-' + idx">
                      <td>{{ pu.type }}</td>
                      <td>{{ pu.number }}</td>
                      <td class="text-right">{{ formatAmount(pu.total) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatAmount(pu.partial) }}
                      </td>
                      <td class="text-center">
                        <a
                          :href="`/compras/index/detalle/${pu.id}`"
                          class="btn btn-sm btn-outline-primary"
                        >
                          Ver
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Otras ordenes de pago asociadas -->
          <div v-if="payorders.length > 0" class="card items-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-stack"></i>
                </span>
                <h5 class="mb-0">Otras ordenes de pago asociadas</h5>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Numero</th>
                      <th class="text-right">Importe</th>
                      <th class="text-right">En orden</th>
                      <th class="text-center">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(po, idx) in payorders" :key="'po-' + idx">
                      <td>{{ po.dateHour }}</td>
                      <td>{{ po.number }}</td>
                      <td class="text-right">{{ formatAmount(po.amount) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatAmount(po.partial) }}
                      </td>
                      <td class="text-center">
                        <router-link
                          :to="`/compras/detalle-orden-de-pago/${po.id}`"
                          class="btn btn-sm btn-outline-primary"
                        >
                          Ver
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals panel -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Total de la orden</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Efectivo</span>
                <span class="value">{{ formatAmount(totalFunds) }}</span>
              </div>
              <div v-if="totalTransfers > 0" class="total-row">
                <span class="label">Transferencia</span>
                <span class="value">{{ formatAmount(totalTransfers) }}</span>
              </div>
              <div class="total-row">
                <span class="label">Cheques 3ros</span>
                <span class="value">{{ formatAmount(totalTpchecks) }}</span>
              </div>
              <div class="total-row">
                <span class="label">Cheques propios</span>
                <span class="value">{{ formatAmount(totalChecks) }}</span>
              </div>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatAmount(payorder.total) }}</span>
              </div>
            </div>
          </div>

          <div v-if="payorder.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota para el proveedor</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ payorder.notes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DownloadDoc from "../../global/DownloadDoc";
import formatDateMixin from "../../../mixins/formatDateMixin";
import { CHECK_KIND_LABELS } from "../../../constants/checkKind";

export default {
  props: ['payOrderId'],
  mixins: [formatDateMixin],
  components: {
    DownloadDoc,
  },
  data() {
    return {
      loading: true,
      pdfurl: process.env.VUE_APP_BASE_URL + '/api/payments/poPDF/' + this.payOrderId,
      payorder: { fund_movement: [], tpchecks: [], checks: [] },
      purchases: [],
      payorders: [],
      payments: [],
      checkKindLabels: CHECK_KIND_LABELS,
    };
  },
  computed: {
    createdDateFormatted() {
      if (!this.payorder.created_at) return '';
      return new Date(this.payorder.created_at).toLocaleDateString('es-AR');
    },
    totalFunds() {
      // type de la caja: 0 = Efectivo, 1 = Banco (transferencia), 2 = Tarjeta.
      return this.payorder.fund_movement
        .filter(mov => !mov.fund || Number(mov.fund.type) !== 1)
        .reduce((acc, mov) => acc + mov.amount, 0);
    },
    totalTransfers() {
      return this.payorder.fund_movement
        .filter(mov => mov.fund && Number(mov.fund.type) === 1)
        .reduce((acc, mov) => acc + mov.amount, 0);
    },
    totalTpchecks() {
      return this.payorder.tpchecks.reduce(
        (acc, mov) => acc + mov.amount,
        0,
      );
    },
    totalChecks() {
      return this.payorder.checks.reduce(
        (acc, mov) => acc + mov.amount,
        0,
      );
    },
  },
  mounted() {
    this.getPayorderDetail();
  },
  methods: {
    formatAmount(value) {
      const n = Number(value) || 0;
      return '$ ' + n.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getPayorderDetail() {
      this.loading = true;
      this.$http
        .get('/api/payments/getPayorderDetail/' + this.payOrderId)
        .then(response => {
          if (response.data.success) {
            this.payorder = response.data.data.payorder;
            const fundMov = this.payorder.fund_movement.map(p => ({
              type: (p.fund && Number(p.fund.type) === 1) ? 'Transferencia' : 'Efectivo',
              detail: p.notes,
              amount: p.amount,
            }));
            const tpchecks = this.payorder.tpchecks.map(p => ({
              type: 'Cheque 3ro',
              detail: `Nº ${p.number} - Bco ${p.bank_name} - Fecha de cobro ${p.date_due}`,
              amount: p.amount,
              check_kind: p.check_kind,
            }));
            const checks = this.payorder.checks.map(p => ({
              type: 'Cheque Propio',
              detail: p.note,
              amount: p.amount,
              check_kind: p.check_kind,
            }));
            this.payments = fundMov.concat(tpchecks).concat(checks);
            this.purchases = response.data.data.purchases;
            this.payorders = response.data.data.payorders;
          }
        })
        .catch(error => {
          console.error('Error al cargar orden de pago:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
